// PasseurDetail — page individuelle pour un Passeur (Intervenant).
// Route : /passeurs/:slug
// Affiche biographie complète, galerie élargie (jusqu'à 12 photos avec lightbox),
// agenda des prestations (au Prieuré et ailleurs) et CTA "Contacter / réserver".
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";
import ShareWidget from "@/components/ShareWidget";
import SeoHead from "@/components/SeoHead";

// Public path used as fallback hero photo when the Passeur has no portrait.
const FALLBACK_HERO = "/photos/prieure-cross.jpg";

const CATEGORY_BACK_LINK = {
  artiste:           { fr: "Passeurs de Lumière",                    en: "Bearers of Light",              path: "/presence/artistes" },
  religieux:         { fr: "Passeurs de l'Indicible",                en: "Bearers of the Ineffable",      path: "/presence/passeurs-foi" },
  conferencier:      { fr: "Passeurs de la Baie",                    en: "Bearers of the Bay",            path: "/presence/passeurs" },
  autre:             { fr: "Passeurs de la Baie",                    en: "Bearers of the Bay",            path: "/presence/passeurs" },
  histoire_sciences: { fr: "Passeurs de l'histoire et des sciences", en: "Bearers of History & Sciences", path: "/presence/passeurs-histoire-sciences" },
};

// ---------- Internal lightbox (re-usable) ----------
function GalleryLightbox({ items, openIdx, onClose, name }) {
  useEffect(() => {
    if (openIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIdx, onClose]);
  if (openIdx === null || !items[openIdx]) return null;
  const current = items[openIdx];
  const prev = (e) => { e.stopPropagation(); onClose(openIdx > 0 ? openIdx - 1 : items.length - 1); };
  const next = (e) => { e.stopPropagation(); onClose(openIdx < items.length - 1 ? openIdx + 1 : 0); };
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(20,16,12,0.94)" }}
      onClick={() => onClose(null)}
      data-testid="passeur-lightbox"
    >
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(null); }}
              className="absolute top-4 right-4 w-10 h-10 inline-flex items-center justify-center font-display text-2xl"
              style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }} aria-label="Close">×</button>
      {items.length > 1 && (
        <>
          <button type="button" onClick={prev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center font-display text-3xl"
                  style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }} aria-label="Previous">‹</button>
          <button type="button" onClick={next}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center font-display text-3xl"
                  style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }} aria-label="Next">›</button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-6xl w-full px-6">
        <img src={current.url} alt={current.caption || name}
             className="w-full max-h-[82vh] object-contain" />
        {current.caption && (
          <p className="font-display italic text-center mt-4" style={{ color: "rgba(255,255,255,0.85)" }}>
            {current.caption}
          </p>
        )}
        <p className="text-center mt-2 font-caps text-[0.65rem]"
           style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.22em" }}>
          {openIdx + 1} / {items.length}
        </p>
      </div>
    </div>
  );
}

export default function PasseurDetail() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);
  useEffect(() => {
    let mounted = true;
    setLoading(true); setNotFound(false);
    api.get(`/passeurs/${slug}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setNotFound(e?.response?.status === 404); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  if (notFound) {
    // Redirect to the artistes index when the slug is invalid
    return <Navigate to="/presence/artistes" replace />;
  }

  if (loading || !data) {
    return (
      <div className="py-32 text-center font-display italic" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }

  const back = CATEGORY_BACK_LINK[data.category] || CATEGORY_BACK_LINK.artiste;
  const heroSrc = data.photo_url || (data.gallery?.[0]?.url) || FALLBACK_HERO;
  const gallery = Array.isArray(data.gallery) ? data.gallery : [];
  const links = Array.isArray(data.links) ? data.links : [];
  const events = Array.isArray(data.upcoming_events) ? data.upcoming_events : [];
  const teaser = (data.bio || "").slice(0, 160).replace(/\s+/g, " ").trim();

  const seoTitles = {
    fr: `${data.name} — Passeurs · Prieuré saint Léonard`,
    en: `${data.name} — Bearers · Priory of Saint Leonard`,
  };
  const seoDesc = {
    fr: teaser || `${data.name}, ${data.function_label}. Fiche du Prieuré saint Léonard.`,
    en: teaser || `${data.name}, ${data.function_label}. Bearer profile at the Priory of Saint Leonard.`,
  };

  const contactHref = `/contact?subject=${encodeURIComponent(
    isFr ? `Contact — ${data.name}` : `Contact — ${data.name}`
  )}`;

  return (
    <div data-testid="passeur-detail">
      <SeoHead titles={seoTitles} descriptions={seoDesc} image={data.photo_url || undefined} />

      {/* HERO ------------------------------------------------------------- */}
      <section className="relative" style={{ height: "62vh", minHeight: 420 }}>
        <img src={heroSrc} alt={data.name} className="absolute inset-0 w-full h-full object-cover"
             style={{ filter: data.photo_url ? "none" : "brightness(0.85)" }} />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.35), rgba(20,16,12,0.78))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <Link to={back.path}
                className="font-caps text-[0.65rem] text-white/80 hover:text-white"
                style={{ letterSpacing: "0.25em" }}
                data-testid="passeur-back-link">
            ← {isFr ? back.fr : back.en}
          </Link>
          <div className="font-caps text-[0.7rem] mt-4"
               style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
            {data.function_label}
          </div>
          <h1 className="font-display text-4xl sm:text-6xl text-white mt-2" data-testid="passeur-name">
            {data.name}
          </h1>
          {(data.city || data.country) && (
            <div className="font-body text-sm text-white/80 mt-2">
              {[data.city, data.country].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </section>

      {/* BIO + GALLERY ---------------------------------------------------- */}
      <section className="py-20 sm:py-28">
        <div className="container-prieure grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left column: bio + links */}
          <div className="lg:col-span-7">
            <Ornament className="mb-6" />
            <div className="font-display whitespace-pre-line leading-relaxed text-lg"
                 style={{ color: "var(--ink-2)" }}
                 data-testid="passeur-bio">
              {data.bio || (isFr ? "Biographie à venir." : "Biography coming soon.")}
            </div>

            {links.length > 0 && (
              <div className="mt-10">
                <div className="font-caps text-[0.7rem] mb-3"
                     style={{ color: "var(--ink)", letterSpacing: "0.25em" }}>
                  {isFr ? "À découvrir" : "Discover"}
                </div>
                <ul className="space-y-2">
                  {links.map((lk, idx) => (
                    <li key={`${lk.url}-${idx}`} className="font-body text-base">
                      <a href={/^https?:\/\//i.test(lk.url) ? lk.url : `https://${lk.url}`}
                         target="_blank" rel="noreferrer noopener"
                         data-testid={`passeur-link-${idx}`}
                         className="link-underline inline-flex items-center gap-2"
                         style={{ color: "var(--cinnabar)" }}>
                        <span aria-hidden="true" style={{ opacity: 0.6 }}>↗</span>
                        {lk.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA contact */}
            <div className="mt-12 border-t pt-8" style={{ borderColor: "var(--border)" }}>
              <p className="font-display italic text-lg mb-4" style={{ color: "var(--ink-2)" }}>
                {isFr
                  ? "Vous souhaitez l'inviter, le rencontrer ou organiser une rencontre au Prieuré ?"
                  : "Would you like to invite them, meet them, or organise a meeting at the Priory?"}
              </p>
              <Link to={contactHref}
                    className="btn btn-primary"
                    data-testid="passeur-contact-cta">
                {isFr ? "Contacter le Prieuré" : "Contact the Priory"}
              </Link>
            </div>
          </div>

          {/* Right column: agenda */}
          <div className="lg:col-span-5">
            {events.length > 0 ? (
              <div className="bg-white border p-6 sm:p-8" style={{ borderColor: "var(--border)" }}
                   data-testid="passeur-agenda">
                <div className="font-caps text-[0.7rem] mb-5"
                     style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
                  {isFr ? "À venir" : "Upcoming"}
                </div>
                <ul className="space-y-6">
                  {events.map((ev, idx) => (
                    <li key={idx} className="border-l-2 pl-4" style={{ borderColor: "var(--cinnabar)" }}
                        data-testid={`passeur-event-${idx}`}>
                      <div className="font-caps text-[0.62rem]"
                           style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                        {ev.date}
                      </div>
                      <div className="font-display text-xl mt-1">{ev.title}</div>
                      {ev.location && (
                        <div className="font-body text-sm mt-1" style={{ color: "var(--muted)" }}>
                          {ev.location}
                        </div>
                      )}
                      {ev.url && (
                        <a href={/^https?:\/\//i.test(ev.url) ? ev.url : `https://${ev.url}`}
                           target="_blank" rel="noreferrer noopener"
                           className="font-caps text-[0.6rem] mt-2 inline-block link-underline"
                           style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                          {isFr ? "EN SAVOIR PLUS →" : "LEARN MORE →"}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-white border p-6 sm:p-8 text-center"
                   style={{ borderColor: "var(--border)" }}>
                <div className="font-caps text-[0.7rem] mb-3"
                     style={{ color: "var(--muted)", letterSpacing: "0.28em" }}>
                  {isFr ? "Agenda" : "Schedule"}
                </div>
                <p className="font-display italic text-base" style={{ color: "var(--muted)" }}>
                  {isFr
                    ? "Aucune date publique annoncée pour le moment."
                    : "No public date announced yet."}
                </p>
              </div>
            )}

            {/* Social icons */}
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              {data.website && (
                <a href={data.website} target="_blank" rel="noreferrer noopener"
                   className="font-caps text-[0.65rem] underline" style={{ color: "var(--lapis)", letterSpacing: "0.22em" }}>
                  {isFr ? "SITE OFFICIEL" : "OFFICIAL SITE"}
                </a>
              )}
              {data.instagram && (
                <a href={data.instagram} target="_blank" rel="noreferrer noopener"
                   className="font-caps text-[0.65rem] underline" style={{ color: "var(--lapis)", letterSpacing: "0.22em" }}>
                  INSTAGRAM
                </a>
              )}
              {data.youtube && (
                <a href={data.youtube} target="_blank" rel="noreferrer noopener"
                   className="font-caps text-[0.65rem] underline" style={{ color: "var(--lapis)", letterSpacing: "0.22em" }}>
                  YOUTUBE
                </a>
              )}
              {data.facebook && (
                <a href={data.facebook} target="_blank" rel="noreferrer noopener"
                   className="font-caps text-[0.65rem] underline" style={{ color: "var(--lapis)", letterSpacing: "0.22em" }}>
                  FACEBOOK
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* EXTENDED GALLERY ------------------------------------------------- */}
      {gallery.length > 0 && (
        <section className="py-16 sm:py-24" style={{ background: "var(--parchment-2)" }}>
          <div className="container-prieure">
            <div className="text-center mb-12">
              <div className="font-caps text-[0.7rem]" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>
                {isFr ? "Au Prieuré · Galerie" : "At the Priory · Gallery"}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3" style={{ color: "var(--ink)" }}>
                {isFr ? "Images choisies" : "Selected images"}
              </h2>
              <Ornament className="mx-auto mt-4" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {gallery.map((g, idx) => (
                <button
                  key={`${g.url}-${idx}`}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  className="block overflow-hidden border bg-white group"
                  style={{ borderColor: "var(--border)", aspectRatio: "1 / 1" }}
                  data-testid={`passeur-gallery-thumb-${idx}`}
                >
                  <img src={g.url}
                       alt={g.caption || `${data.name} — ${idx + 1}`}
                       loading="lazy"
                       className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <GalleryLightbox items={gallery} openIdx={lightboxIdx} onClose={setLightboxIdx} name={data.name} />
      <ShareWidget />
    </div>
  );
}
